import { matchPath } from 'react-router';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';

import { navigateToGuidelineDetails } from 'actions/navigateActions';
import {

  FETCH_DEALER_FAILURE,
  FETCH_DEALER_SUCCESS,
  SELECT_DEALER,
} from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import { scopeToPage } from 'modules/app/sagas/utils';
import { fetchBrandsByDealer, fetchBrandsByGroup } from 'modules/brands/actions';
import { FETCH_BRANDS_BY_DEALER_FAILURE,
  FETCH_BRANDS_BY_DEALER_SUCCESS,
  FETCH_BRANDS_BY_GROUP_FAILURE,
  FETCH_BRANDS_BY_GROUP_SUCCESS, } from 'modules/brands/actionsType';
import type { Action } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';
import { SELECT_GROUP } from 'modules/groups/actionTypes';
import { getSelectedGroupId } from 'modules/groups/selectors';
import * as actions from 'modules/guidelines/actions';
import * as actionTypes from 'modules/guidelines/actionTypes';
import { DEALER_DASHBOARD_SOURCE, GROUP_DASHBOARD_SOURCE } from 'modules/guidelines/constants';
import { getParams as getDealerParams } from 'modules/guidelines/selectors/ui/dashboard/dealer';
import { getParams as getGroupParams } from 'modules/guidelines/selectors/ui/dashboard/group';
import { getSelectedDealer } from 'selectors/dealerSelectors';
import { getCurrentLocation } from 'selectors/router';
import { isUndefined } from 'utils/global';

import type { CloneGuidelineSuccessAction, GuidelineQueryParams, OpenAssignmentModalAction } from '../types/actions';

// The dealer guideline list is only relevant on the dealer config / dashboard pages.
const DEALER_LOCATION = [PATH.DEALER_CONFIG_PAGE, PATH.GUIDELINES_DASHBOARD];

function* refreshDashboard() {
  const groupParams: GuidelineQueryParams = yield select(getGroupParams);
  const dealerParams: GuidelineQueryParams = yield select(getDealerParams);
  const currentLocation: ReturnType<typeof getCurrentLocation> = yield select(getCurrentLocation);

  yield put(actions.fetchGuidelines(groupParams, { source: GROUP_DASHBOARD_SOURCE }));

  if (DEALER_LOCATION.some(location => matchPath(location.path, currentLocation))) {
    yield put(actions.fetchGuidelines(dealerParams, { source: DEALER_DASHBOARD_SOURCE }));
  }
}

function* onDealerOrGroupChange() {
  const { error }: Action = yield take([FETCH_DEALER_SUCCESS, FETCH_DEALER_FAILURE]);
  if (!error) {
    const dealer: { group?: { id?: string }; groupId?: string } = yield select(getSelectedDealer);
    const selectedGroupId: MayBe<string> = yield select(getSelectedGroupId);
    const dealerGroupId = dealer.group?.id ?? dealer.groupId;
    if (selectedGroupId === dealerGroupId) {
      yield call(refreshDashboard);
    }
  }
}

// Clone is launched from the dashboard duplicate modal; on success, redirect to the
// cloned guideline. Scoped to the dashboard, so the redirect only happens when the
// clone was triggered there.
function* afterCloneGuideline({ payload: guideline }: CloneGuidelineSuccessAction) {
  const origin: ReturnType<typeof getCurrentLocation> = yield select(getCurrentLocation);
  const isGroup = matchPath(PATH.GROUP_GUIDELINES_CONFIG.path, origin) !== null;
  yield call(navigateToGuidelineDetails, guideline.guidelineId, { origin }, isGroup);
}

// Assignment modal (opened from the dashboard): load the guideline + the relevant
// brands, then mark the modal ready.
function* afterOpenAssignmentModal({ payload, meta }: OpenAssignmentModalAction) {
  const { guidelineId, groupId, dealerId } = payload;
  try {
    yield put(actions.fetchGuideline(guidelineId));
    const response: Action = yield take([actionTypes.FETCH_GUIDELINE_SUCCESS, actionTypes.FETCH_GUIDELINE_FAILURE]);
    if (response.type === actionTypes.FETCH_GUIDELINE_FAILURE) {
      throw response.payload;
    }

    if (!isUndefined(groupId)) {
      yield put(fetchBrandsByGroup(groupId, { projection: 'page' }));
      // The brands failure is auto-generated by simpleRequest: its payload IS the Axios
      // error (there is no `.error` field on it — reading it used to throw `undefined`
      // and lose the actual error).
      const result: Action = yield take([FETCH_BRANDS_BY_GROUP_SUCCESS, FETCH_BRANDS_BY_GROUP_FAILURE]);
      if (result.type === FETCH_BRANDS_BY_GROUP_FAILURE) {
        throw result.payload;
      }
    }

    if (!isUndefined(dealerId)) {
      yield put(fetchBrandsByDealer(dealerId));
      const result: Action = yield take([FETCH_BRANDS_BY_DEALER_SUCCESS, FETCH_BRANDS_BY_DEALER_FAILURE]);
      if (result.type === FETCH_BRANDS_BY_DEALER_FAILURE) {
        throw result.payload;
      }
    }
    yield put(actions.isAssignmentModalReady(undefined, meta));
  } catch (error) {
    yield put(actions.isAssignmentModalFailure(error, meta));
  }
}

function* onDashboardPage() {
  yield call(refreshDashboard);
  yield takeLatest([actionTypes.SET_DASHBOARD_FILTER, SELECT_GROUP], refreshDashboard);
  yield takeLatest(SELECT_DEALER, onDealerOrGroupChange);
  yield takeLatest(actionTypes.POST_CLONE_GUIDELINE_SUCCESS, afterCloneGuideline);
  yield takeLatest(actionTypes.OPEN_ASSIGN_MODAL, afterOpenAssignmentModal);
}

export default function* dashboardSaga() {
  yield scopeToPage([PATH.GUIDELINES_DASHBOARD, PATH.GROUP_GUIDELINES_CONFIG], onDashboardPage);
}
